import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasscodesDeviceDto } from 'services/passcodes/passcodes.service';
import { CreateSingleUsePassResponse } from 'services/single-use-passes/single-use-passes.service';
import { useAPISingleUsePasses } from 'services/single-use-passes/api-single-use-passes.service';
import { onSharePasscode } from 'mobile/passcodes/passcode-functions';
import { SingleUsePassCreationController, WhenMode } from './interfaces';
import { formatSince, nowSince, windowEndLabel } from './date-helpers';
import { buildShareMessage } from './share-message';

export const useSingleUsePassCreationController = (
    service = useAPISingleUsePasses(),
): SingleUsePassCreationController => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const accessToken = searchParams.get('access_token') || '';

    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
        }
    }, []);

    const [step, setStep] = useState<0 | 1>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasNetworkError, setHasNetworkError] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedLock, setSelectedLock] = useState<PasscodesDeviceDto | undefined>();
    const [whenMode, setWhenMode] = useState<WhenMode>('now');
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [label, setLabel] = useState('');
    const [created, setCreated] = useState<CreateSingleUsePassResponse | undefined>();

    const canSubmit = useMemo(
        () => !!selectedLock && !isLoading && (whenMode === 'now' || (!!scheduledDate && !!scheduledTime)),
        [selectedLock, isLoading, whenMode, scheduledDate, scheduledTime],
    );

    const computeSince = (): string => {
        if (whenMode === 'now') {
            // "Ahora" en el wall-clock del SITIO (TZ del lock), no del device — el backend
            // reinterpreta el "Z" en la TZ del lock, así evitamos el corrimiento si difieren.
            const n = nowSince(selectedLock?.timezone);
            return formatSince(n.date, n.time);
        }
        return formatSince(scheduledDate, scheduledTime);
    };

    const validUntil = useMemo((): string => {
        if (whenMode === 'now') {
            if (!selectedLock) {
                return '';
            }
            const n = nowSince(selectedLock.timezone);
            return windowEndLabel(n.date, n.time);
        }
        return scheduledDate && scheduledTime ? windowEndLabel(scheduledDate, scheduledTime) : '';
    }, [whenMode, scheduledDate, scheduledTime, selectedLock]);

    const submit = () => {
        if (!selectedLock) {
            return;
        }
        setIsLoading(true);
        setHasNetworkError(false);
        service
            .createSingleUsePass(accessToken, { lock_id: selectedLock.id, label, since: computeSince() })
            .then((r) => {
                setCreated(r);
                setStep(1);
            })
            .catch(() => setHasNetworkError(true))
            .finally(() => setIsLoading(false));
    };

    const onShare = () => {
        if (!created || !selectedLock) {
            return;
        }
        onSharePasscode({
            v: 1,
            data: { message: buildShareMessage(created.code, created.share_url, selectedLock.lock_name) },
        });
    };
    const onShareCodeOnly = () => {
        if (!created) {
            return;
        }
        onSharePasscode({ v: 1, data: { message: created.code } });
    };
    const reset = () => {
        setStep(0);
        setCreated(undefined);
        setSelectedLock(undefined);
        setWhenMode('now');
        setScheduledDate('');
        setScheduledTime('');
        setLabel('');
    };

    return {
        step,
        isLoading,
        hasNetworkError,
        pickerOpen,
        selectedLock,
        whenMode,
        scheduledDate,
        scheduledTime,
        label,
        created,
        canSubmit,
        validUntil,
        openPicker: () => setPickerOpen(true),
        closePicker: () => setPickerOpen(false),
        onLockPicked: (lock) => {
            setSelectedLock(lock);
            setPickerOpen(false);
        },
        setWhenMode,
        onPickedDate: setScheduledDate,
        onPickedTime: setScheduledTime,
        setLabel,
        submit,
        onShare,
        onShareCodeOnly,
        reset,
    };
};
